/**
 * "Run on this dataset": a schedule-fired action target bound to one dataset.
 *
 * The runtime gives a scheduled invocation no data source, only the target's
 * input query, so binding a dataset is spelled as that query: the dataset's
 * id, and any path patterns as `LIKE` matches. This class owns that spelling
 * in both directions, so the editor can show a dataset picker and pattern
 * list over what is really a stored query string.
 */

import { ACCEPT_ALL_PATTERN } from "./acceptAllPattern";

const DATASET_CLAUSE = /^dataset_id = "([^"]+)"(?: AND \((.+)\))?$/;
const LIKE_CLAUSE = /^path LIKE "((?:[^"\\]|\\.)*)"$/;

export class DatasetBinding {
  public constructor(
    public readonly datasetId: string,
    /** Glob-style patterns, `**​/*.mcap`; empty means every file. */
    public readonly patterns: readonly string[],
  ) {}

  /** The query this binding stores, or `null` when no dataset is chosen yet. */
  public toQuery(): string {
    const patterns = this.patterns
      .map((pattern) => pattern.trim())
      .filter(
        (pattern) => pattern.length > 0 && pattern !== ACCEPT_ALL_PATTERN,
      );
    const base = `dataset_id = "${this.datasetId}"`;
    if (patterns.length === 0) {
      return base;
    }
    const matches = patterns
      .map((pattern) => `path LIKE "${DatasetBinding.globToLike(pattern)}"`)
      .join(" OR ");
    return `${base} AND (${matches})`;
  }

  /**
   * Reads a stored query back, or `null` when it is not one this class
   * wrote: a hand-written query stays a query.
   */
  public static fromQuery(
    query: string | null | undefined,
  ): DatasetBinding | null {
    if (query === null || query === undefined) {
      return null;
    }
    const match = DATASET_CLAUSE.exec(query.trim());
    if (match === null) {
      return null;
    }
    const [, datasetId, clauses] = match;
    if (clauses === undefined) {
      return new DatasetBinding(datasetId, []);
    }
    const patterns: string[] = [];
    for (const clause of clauses.split(" OR ")) {
      const like = LIKE_CLAUSE.exec(clause.trim());
      if (like === null) {
        return null;
      }
      patterns.push(DatasetBinding.likeToGlob(like[1]));
    }
    return new DatasetBinding(datasetId, patterns);
  }

  /** `**​/*.mcap` → `%.mcap`; `?` → `_`. A LIKE match has no directory notion, so `**​/` folds into `%`. */
  public static globToLike(pattern: string): string {
    return pattern
      .replace(/%/g, "\\%")
      .replace(/_/g, "\\_")
      .replace(/\*\*\//g, "*")
      .replace(/\*+/g, "%")
      .replace(/\?/g, "_");
  }

  public static likeToGlob(pattern: string): string {
    return pattern.replace(
      /\\([%_])|([%_])/g,
      (_match: string, escaped?: string, wildcard?: string) => {
        if (escaped !== undefined) {
          return escaped;
        }
        return wildcard === "%" ? "*" : "?";
      },
    );
  }
}
